import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Switch,
  Select,
  message,
  Space,
  Popconfirm,
  Tag,
  Card,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { apiClient } from '../../../config/apiClient';
import MainLayout from '../../layout/layout';

const CanchasClub = () => {
  const [canchas, setCanchas] = useState([]);
  const [tiposDeporte, setTiposDeporte] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCancha, setEditingCancha] = useState(null);
  const [form] = Form.useForm();
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);

  // Cargar tipos de deporte
  const loadTiposDeporte = async () => {
    try {
      const response = await apiClient.get('/club/tipos-deporte/activos');
      if (response.ok) {
        const data = await response.json();
        setTiposDeporte(data);
      }
    } catch (error) {
      console.error('Error al cargar tipos de deporte:', error);
    }
  };

  // Cargar canchas
  const loadCanchas = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/club/canchas');
      if (response.ok) {
        const data = await response.json();
        setCanchas(data);
      } else {
        message.error('Error al cargar las canchas del club');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al cargar las canchas del club');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTiposDeporte();
    loadCanchas();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Abrir modal para crear nueva cancha
  const handleCreate = () => {
    setEditingCancha(null);
    form.resetFields();
    form.setFieldsValue({ activa: true, techada: false });
    setModalVisible(true);
  };

  // Abrir modal para editar cancha
  const handleEdit = (record) => {
    setEditingCancha(record);
    form.setFieldsValue({
      nombre: record.ccl_nombre,
      tipoDeporte: record.ccl_tipo_deporte,
      techada: record.ccl_techada,
      activa: record.ccl_activa,
    });
    setModalVisible(true);
  };

  // Guardar cancha (crear o actualizar)
  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      
      if (editingCancha) {
        // Actualizar
        const response = await apiClient.put(`/club/canchas/${editingCancha.ccl_id}`, values);
        if (response.ok) {
          message.success('Cancha del club actualizada correctamente');
          setModalVisible(false);
          loadCanchas();
        } else {
          const error = await response.json();
          message.error(error.message || 'Error al actualizar la cancha del club');
        }
      } else {
        // Crear
        const response = await apiClient.post('/club/canchas', values);
        if (response.ok) {
          message.success('Cancha del club creada correctamente');
          setModalVisible(false);
          loadCanchas();
        } else {
          const error = await response.json();
          message.error(error.message || 'Error al crear la cancha del club');
        }
      }
    } catch (error) {
      console.error('Error:', error);
      if (error.errorFields) {
        return;
      }
      message.error('Error al guardar la cancha del club');
    }
  };

  // Eliminar cancha
  const handleDelete = async (id) => {
    try {
      const response = await apiClient.delete(`/club/canchas/${id}`);
      if (response.ok) {
        message.success('Cancha del club eliminada correctamente');
        loadCanchas();
      } else {
        const error = await response.json();
        message.error(error.message || 'Error al eliminar la cancha del club');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al eliminar la cancha del club');
    }
  };

  // Columnas de la tabla
  const columns = [
    {
      title: 'Nombre',
      dataIndex: 'ccl_nombre',
      key: 'ccl_nombre',
    },
    {
      title: 'Tipo de Deporte',
      dataIndex: 'tipo_deporte_nombre',
      key: 'tipo_deporte_nombre',
      render: (text, record) => {
        const duracion = record.tipo_deporte_duracion;
        const horas = Math.floor(duracion / 60);
        const mins = duracion % 60;
        const duracionTexto = horas > 0 && mins > 0 ? `${horas}h ${mins}m` : horas > 0 ? `${horas}h` : `${mins}m`;
        return (
          <span>
            <Tag color="blue">{text}</Tag>
            <Tag color="geekblue">{duracionTexto}/turno</Tag>
          </span>
        );
      },
    },
    {
      title: 'Techada',
      dataIndex: 'ccl_techada',
      key: 'ccl_techada',
      width: 100,
      render: (techada) => (
        <Tag color={techada ? 'green' : 'default'}>
          {techada ? 'Sí' : 'No'}
        </Tag>
      ),
    },
    {
      title: 'Estado',
      dataIndex: 'ccl_activa',
      key: 'ccl_activa',
      width: 120,
      render: (activa) => (
        <Tag color={activa ? 'green' : 'red'}>
          {activa ? 'Activa' : 'Inactiva'}
        </Tag>
      ),
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 150,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          >
            Editar
          </Button>
          <Popconfirm
            title="¿Está seguro de eliminar esta cancha?"
            onConfirm={() => handleDelete(record.ccl_id)}
            okText="Sí"
            cancelText="No"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              size="small"
            >
              Eliminar
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <MainLayout>
      <Card
        title="Gestión de Canchas del Club"
        extra={
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={loadCanchas}
              loading={loading}
            >
              Actualizar
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreate}
            >
              Nueva Cancha
            </Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={canchas}
          loading={loading}
          rowKey="ccl_id"
          scroll={{ x: isMobile ? 800 : 'auto' }}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total: ${total} canchas`,
          }}
        />

        <Modal
          title={editingCancha ? 'Editar Cancha del Club' : 'Nueva Cancha del Club'}
          open={modalVisible}
          onOk={handleSave}
          onCancel={() => setModalVisible(false)}
          okText="Guardar"
          cancelText="Cancelar"
          width={isMobile ? '90%' : 600}
        >
          <Form
            form={form}
            layout="vertical"
            initialValues={{ activa: true, techada: false }}
          >
            <Form.Item
              name="nombre"
              label="Nombre"
              rules={[
                { required: true, message: 'El nombre es requerido' },
                { max: 100, message: 'El nombre no puede exceder 100 caracteres' },
              ]}
            >
              <Input placeholder="Ej: Cancha 1" />
            </Form.Item>

            <Form.Item
              name="tipoDeporte"
              label="Tipo de Deporte"
              rules={[{ required: true, message: 'El tipo de deporte es requerido' }]}
            >
              <Select placeholder="Seleccione un tipo de deporte">
                {tiposDeporte.map((tipo) => {
                  const dur = tipo.tdp_duracion_minutos || 90;
                  const h = Math.floor(dur / 60);
                  const m = dur % 60;
                  const durTxt = h > 0 && m > 0 ? `${h}h ${m}m` : h > 0 ? `${h}h` : `${m}m`;
                  return (
                    <Select.Option key={tipo.tdp_id} value={tipo.tdp_id}>
                      {tipo.tdp_nombre} ({durTxt}/turno)
                    </Select.Option>
                  );
                })}
              </Select>
            </Form.Item>

            <Form.Item
              name="techada"
              label="Techada"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>

            <Form.Item
              name="activa"
              label="Activa"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          </Form>
        </Modal>
      </Card>
    </MainLayout>
  );
};

export default CanchasClub;
